/**
 * Session Manager Hook
 *
 * Basit session tracking:
 * - User login = session_start
 * - Tab close = session_end (beforeunload)
 * - Logout = session_end
 *
 * Bu hook App.tsx veya DashboardLayout içinde bir kez çağrılmalı.
 *
 * @see USER_BEHAVIOR_ANALYTICS_ROADMAP.md
 */

import { useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { eventTracker } from '../services/eventTracker';

export function useSessionManager() {
  const { user } = useAuth();
  const sessionStartedRef = useRef(false);
  const userIdRef = useRef<string | null>(null);

  useEffect(() => {
    // No user or anonymous user - don't track
    if (!user?.id || user.userType === 'anonymous') {
      return;
    }

    // Store userId for cleanup
    userIdRef.current = user.id;

    // Start session only once per mount
    if (!sessionStartedRef.current) {
      eventTracker.startSession(user.id);
      sessionStartedRef.current = true;

      if (process.env.NODE_ENV === 'development') {
        console.log('[SessionManager] Session started for user:', user.id);
      }
    }

    // Tab close / page unload handler
    const handleBeforeUnload = () => {
      if (userIdRef.current) {
        eventTracker.endSession(userIdRef.current);

        if (process.env.NODE_ENV === 'development') {
          console.log('[SessionManager] Session ended (beforeunload)');
        }
      }
    };

    // Visibility change handler (optional - track when user leaves tab)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden' && userIdRef.current) {
        // Could optionally track tab switches here
        // For now, we just log for debugging
        if (process.env.NODE_ENV === 'development') {
          console.log('[SessionManager] Tab hidden');
        }
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [user?.id, user?.userType]);

  // Cleanup on unmount (e.g., logout navigation)
  useEffect(() => {
    return () => {
      if (sessionStartedRef.current && userIdRef.current) {
        eventTracker.endSession(userIdRef.current);
        sessionStartedRef.current = false;

        if (process.env.NODE_ENV === 'development') {
          console.log('[SessionManager] Session ended (unmount)');
        }
      }
    };
  }, []);

  // Return session info for debugging
  return {
    sessionId: eventTracker.getSessionId(),
    isTracking: sessionStartedRef.current,
  };
}
